import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Copy, Bitcoin, Wallet, Check, Clock, X as XIcon, Plus } from "lucide-react";

export default function Billing() {
  const [wallets, setWallets] = useState(null);
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    amount_usd: 10, currency: "BTC", tx_hash: "", paid_at: new Date().toISOString().slice(0, 16), note: "",
  });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [me, w, mine] = await Promise.all([
        api.get("/auth/me"),
        api.get("/billing/wallets"),
        api.get("/payments/mine"),
      ]);
      setUser(me.data);
      setWallets(w.data);
      setClaims(mine.data);
    } catch {
      navigate("/agent/login");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const copy = (v) => {
    navigator.clipboard.writeText(v);
    toast.success("Copied to clipboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const paid_at_iso = form.paid_at ? new Date(form.paid_at).toISOString() : new Date().toISOString();
      const { data } = await api.post("/payments/claim", { ...form, amount_usd: Number(form.amount_usd), paid_at: paid_at_iso });
      setClaims((prev) => [data.claim, ...prev]);
      toast.success("Payment reported — admin has been notified");
      setShowForm(false);
      setForm({ amount_usd: 10, currency: "BTC", tx_hash: "", paid_at: new Date().toISOString().slice(0, 16), note: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const trialEnd = user?.trial_end ? new Date(user.trial_end) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 0;
  const statusBadge = (s) => s === "verified" ? { icon: Check, cls: "bg-black text-white" } : s === "rejected" ? { icon: XIcon, cls: "hairline text-muted-foreground" } : { icon: Clock, cls: "bg-[var(--btc)] text-black" };

  return (
    <div className="min-h-screen bg-[var(--paper)] p-6 sm:p-10 max-w-3xl mx-auto">
      <Link to="/agent/dashboard" className="text-xs tracking-[0.25em] uppercase font-bold hover:underline underline-offset-4">← Dashboard</Link>
      <h1 className="mt-8 font-display text-4xl sm:text-5xl font-black tracking-tighter">Billing</h1>
      <p className="mt-2 text-muted-foreground text-sm">$10 / month · Pay in BTC or ETH after your trial ends</p>

      <section className="mt-8 hairline bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Current plan</div>
            <div className="font-display text-2xl font-medium mt-1">{user?.subscription_status === "active" ? "Active" : "Free Trial"} · {daysLeft} days left</div>
          </div>
          <div className="text-right font-mono text-sm">{trialEnd?.toDateString()}</div>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="hairline bg-white p-6" style={{ borderLeft: "3px solid var(--btc)" }}>
          <div className="flex items-center gap-2 mb-4"><Bitcoin className="w-4 h-4" style={{ color: "var(--btc)" }} /><span className="text-[10px] tracking-[0.3em] uppercase font-bold">Bitcoin</span></div>
          <div className="text-xs text-muted-foreground mb-1">Wallet address</div>
          <div className="font-mono text-sm break-all bg-[var(--paper-2)] p-3">{wallets?.btc || "…"}</div>
          <button data-testid="copy-btc" onClick={() => copy(wallets?.btc)} className="mt-4 flex items-center gap-2 text-xs hover:underline"><Copy className="w-3.5 h-3.5" /> Copy address</button>
        </div>

        <div className="hairline bg-white p-6" style={{ borderLeft: "3px solid var(--eth)" }}>
          <div className="flex items-center gap-2 mb-4"><Wallet className="w-4 h-4" style={{ color: "var(--eth)" }} /><span className="text-[10px] tracking-[0.3em] uppercase font-bold">Ethereum</span></div>
          <div className="text-xs text-muted-foreground mb-1">Wallet address</div>
          <div className="font-mono text-sm break-all bg-[var(--paper-2)] p-3">{wallets?.eth || "…"}</div>
          <button data-testid="copy-eth" onClick={() => copy(wallets?.eth)} className="mt-4 flex items-center gap-2 text-xs hover:underline"><Copy className="w-3.5 h-3.5" /> Copy address</button>
        </div>
      </div>

      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-medium">Report a payment</h2>
          {!showForm && (
            <button data-testid="report-payment-btn" onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-black text-white px-4 py-2 text-xs uppercase tracking-[0.2em] font-bold">
              <Plus className="w-3.5 h-3.5" /> New report
            </button>
          )}
        </div>

        {showForm && (
          <motion.form
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            className="hairline bg-white p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
            data-testid="payment-form"
          >
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Amount (USD)</label>
              <input data-testid="pay-amount" type="number" min="0" step="0.01" value={form.amount_usd} onChange={(e) => setForm((f) => ({ ...f, amount_usd: e.target.value }))} className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Currency</label>
              <select data-testid="pay-currency" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className="w-full mt-2 px-3 py-2 hairline bg-white focus:border-black outline-none">
                <option value="BTC">Bitcoin (BTC)</option>
                <option value="ETH">Ethereum (ETH)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Transaction hash</label>
              <input data-testid="pay-tx" placeholder="0x… or bitcoin txid" value={form.tx_hash} onChange={(e) => setForm((f) => ({ ...f, tx_hash: e.target.value }))} className="w-full mt-2 px-3 py-2 hairline font-mono text-sm focus:border-black outline-none" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Paid at</label>
              <input data-testid="pay-date" type="datetime-local" value={form.paid_at} onChange={(e) => setForm((f) => ({ ...f, paid_at: e.target.value }))} className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Note (optional)</label>
              <input data-testid="pay-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="w-full mt-2 px-3 py-2 hairline focus:border-black outline-none" />
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 hairline">Cancel</button>
              <button data-testid="pay-submit" disabled={busy} type="submit" className="px-5 py-2 bg-black text-white disabled:opacity-40">{busy ? "Sending…" : "Notify admin"}</button>
            </div>
          </motion.form>
        )}

        {claims.length === 0 ? (
          <p className="text-sm text-muted-foreground">No payments reported yet. When you pay via BTC/ETH, click &ldquo;New report&rdquo; so we can activate your subscription.</p>
        ) : (
          <div className="flex flex-col gap-3" data-testid="my-payments-list">
            {claims.map((c) => {
              const b = statusBadge(c.status);
              const Icon = b.icon;
              return (
                <div key={c.claim_id} data-testid={`my-payment-${c.claim_id}`} className="hairline bg-white p-4 flex items-center gap-4">
                  <div className={`w-8 h-8 grid place-items-center ${b.cls}`}><Icon className="w-3.5 h-3.5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono">${c.amount_usd} · {c.currency}</span>
                      <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-muted-foreground">{c.status}</span>
                    </div>
                    {c.tx_hash && <div className="text-xs text-muted-foreground font-mono truncate">{c.tx_hash}</div>}
                    <div className="text-xs text-muted-foreground">{c.paid_at ? new Date(c.paid_at).toLocaleString() : ""}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <p className="mt-8 text-xs text-muted-foreground">
        After you pay $10 equivalent in BTC or ETH to the wallets above, report the transaction here. The platform admin will verify it and extend your subscription by 30 days.
      </p>
    </div>
  );
}
